// pages/NowPlaying.jsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Button,
  Rating,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  TextField,
  Pagination
} from '@mui/material';
import { Link } from 'react-router-dom';
import { FaSearch, FaStar, FaRegCalendarAlt, FaTicketAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMovies } from '../context/MovieContext';

const NowPlaying = () => {
  const { nowPlayingMovies, loading, error, getImageUrl } = useMovies();
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [sortOption, setSortOption] = useState('popularity');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const moviesPerPage = 8;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const movieCardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: { 
      y: -10,
      boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.3)",
      transition: { duration: 0.3 }
    }
  };

  // Filter and sort movies based on user selections
  useEffect(() => {
    if (!nowPlayingMovies) return;

    let result = [...nowPlayingMovies];
    
    // Apply search filter
    if (searchQuery.trim()) {
      result = result.filter(movie => 
        movie.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply sorting
    switch (sortOption) {
      case 'title':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'rating':
        result.sort((a, b) => b.vote_average - a.vote_average);
        break;
      case 'release_date':
        result.sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
        break;
      case 'popularity':
      default:
        result.sort((a, b) => b.popularity - a.popularity);
        break;
    }
    
    setFilteredMovies(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [nowPlayingMovies, sortOption, searchQuery]);

  // Get current movies for pagination
  const indexOfLastMovie = currentPage * moviesPerPage;
  const indexOfFirstMovie = indexOfLastMovie - moviesPerPage;
  const currentMovies = filteredMovies.slice(indexOfFirstMovie, indexOfLastMovie);
  const totalPages = Math.ceil(filteredMovies.length / moviesPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of the movie grid
    window.scrollTo({
      top: document.getElementById('movie-grid').offsetTop - 100,
      behavior: 'smooth',
    });
  };

  // Format release date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Box>
      <Navbar />
      
      <Box 
        sx={{
          py: 10,
          background: 'linear-gradient(to bottom, #0a1929 0%, #152238 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h2" 
              align="center" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                mb: 1
              }}
            >
              Now Playing
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ 
                mb: 6,
                opacity: 0.8,
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Discover the latest blockbusters now showing at Stellar Cinemas
            </Typography>
          </motion.div>

          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={9}>
                <TextField
                  placeholder="Search movies..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch color="#f5b400" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl 
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }
                  }}
                >
                  <InputLabel id="sort-select-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    value={sortOption}
                    label="Sort By"
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <MenuItem value="popularity">Popularity</MenuItem>
                    <MenuItem value="rating">Rating</MenuItem>
                    <MenuItem value="release_date">Release Date</MenuItem>
                    <MenuItem value="title">Title</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </motion.div>

          {/* Movie Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : error ? (
            <Typography variant="h5" color="error" align="center" sx={{ py: 8 }}>
              {error}
            </Typography>
          ) : filteredMovies.length === 0 ? (
            <Typography variant="h6" align="center" sx={{ py: 8 }}>
              No movies match your search. Please try different keywords.
            </Typography>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <Grid container spacing={4} id="movie-grid">
                  {currentMovies.map((movie) => (
                    <Grid item xs={12} sm={6} md={3} key={movie.id}>
                      <motion.div variants={movieCardVariants} whileHover="hover">
                        <Card 
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(21, 34, 56, 0.8)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          {/* Movie Poster */}
                          <Box 
                            sx={{ 
                              position: 'relative', 
                              overflow: 'hidden',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '30%',
                                background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                              },
                              '&:hover::after': {
                                opacity: 1
                              }
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={getImageUrl(movie.poster_path, 'w500')}
                              alt={movie.title}
                              sx={{ 
                                height: 350,
                                transition: 'all 0.5s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: '#f5b400',
                                borderRadius: '12px',
                                padding: '4px 8px',
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(245, 180, 0, 0.3)'
                              }}
                            >
                              {movie.vote_average.toFixed(1)}
                            </Box>
                          </Box>
                          
                          {/* Content */}
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            {/* Rating */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 1,
                                gap: 1 
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {[...Array(10)].map((_, index) => {
                                  const starValue = index + 1;
                                  const filled = movie.vote_average >= starValue;
                                  const partialFill = !filled && movie.vote_average > index && movie.vote_average < starValue;
                                  
                                  return (
                                    <Box 
                                      key={index} 
                                      sx={{ 
                                        position: 'relative', 
                                        display: 'inline-flex',
                                        color: filled ? '#f5b400' : 'rgba(245, 180, 0, 0.3)'
                                      }}
                                    >
                                      <FaStar size={12} />
                                      {partialFill && (
                                        <Box 
                                          sx={{ 
                                            position: 'absolute', 
                                            top: 0, 
                                            left: 0, 
                                            width: `${(movie.vote_average - index) * 100}%`,
                                            overflow: 'hidden',
                                            display: 'inline-flex'
                                          }}
                                        >
                                          <FaStar size={12} style={{ color: '#f5b400' }} />
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>

                            {/* Title */}
                            <Typography 
                              variant="h6" 
                              gutterBottom 
                              sx={{ 
                                fontWeight: 600,
                                lineHeight: 1.3,
                                fontSize: '1.1rem',
                                height: '2.6rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {movie.title}
                            </Typography>
                            
                            {/* Release Date */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 2,
                              color: 'text.secondary'
                            }}>
                              <FaRegCalendarAlt style={{ marginRight: 8, color: '#f5b400' }} />
                              <Typography variant="body2">
                                {formatDate(movie.release_date)}
                              </Typography>
                            </Box>
                            
                            {/* Overview snippet */}
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                mb: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                height: '4.5em'
                              }}
                            >
                              {movie.overview}
                            </Typography>
                          </CardContent>
                          
                          {/* Actions */}
                          <CardActions sx={{ p: 2, pt: 0 }}>
                            <Link 
                              to={`/movie/${movie.id}`} 
                              style={{ 
                                width: '100%', 
                                textDecoration: 'none' 
                              }}
                            >
                              <Button 
                                component={motion.button}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                variant="contained" 
                                color="secondary"
                                fullWidth
                                startIcon={<FaTicketAlt />}
                                sx={{
                                  borderRadius: '8px',
                                  fontWeight: 600,
                                  py: 1
                                }}
                              >
                                Buy Tickets
                              </Button>
                            </Link>
                          </CardActions>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <Pagination 
                    count={totalPages} 
                    page={currentPage} 
                    onChange={handlePageChange} 
                    color="secondary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: 'text.primary',
                      },
                      '& .Mui-selected': {
                        backgroundColor: 'rgba(245, 180, 0, 0.2) !important',
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default NowPlaying;